import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import ReactSelect from "react-select";
import type { MultiValue, StylesConfig } from "react-select";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Badge } from "../../components/ui/Badge";
import { Table } from "../../components/ui/Table";
import { Pagination } from "../../components/ui/Pagination";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

interface ClienteRow {
  id: number;
  razonSocial?: string;
  razon_social?: string;
  cif?: string;
  personaContacto?: string;
  persona_contacto?: string;
  telefono?: string;
  email?: string;
  estado?: string;
  metodoPago?: string;
  metodo_pago?: string;
  numeroPoliza?: string | null;
  numero_poliza?: string | null;
  coberturaAutoAprobada?: boolean;
  cobertura_auto_aprobada?: boolean;
  [key: string]: unknown;
}

interface ClienteDetail {
  id: number;
  razonSocial?: string;
  razon_social?: string;
  cif?: string;
  direccion?: string;
  poblacion?: string;
  codigoPostal?: string;
  codigo_postal?: string;
  pais?: string;
  personaContacto?: string;
  persona_contacto?: string;
  telefono?: string;
  emailContacto?: string;
  email_contacto?: string;
  email?: string;
  numeroPoliza?: string | null;
  numero_poliza?: string | null;
  metodoPago?: string;
  metodo_pago?: string;
  coberturaAutoAprobada?: boolean;
  cobertura_auto_aprobada?: boolean;
  estado?: string;
  modalidades?: ModalidadAsignada[];
  totalSeguros?: number;
  segurosPagados?: number;
  [key: string]: unknown;
}

interface ModalidadAsignada {
  id: number;
  nombre: string;
  tipo: string;
  precio?: number;
  precio_base?: number;
  precio_personalizado?: number | null;
  precio_efectivo?: number;
  precioPersonalizado?: number | null;
}

interface ModalidadOption {
  id: number;
  nombre: string;
  tipo: string;
  precio?: number;
  isVisible?: boolean;
  is_visible?: number;
}

interface ModalidadSelectOption {
  value: number;
  label: string;
  tipo: string;
  precioBase: number;
}

interface ClientePreferenciasConfig {
  recibos_individuales_activos: boolean;
  recibos_mensuales_activos: boolean;
}

interface SeguroRow {
  id: number;
  eventoReferencia?: string | null;
  evento_referencia?: string | null;
  fechaInicio?: string;
  fecha_inicio?: string;
  estado?: string;
  importeTotal?: number;
  importe_total?: number;
  modalidad?: { nombre: string };
}

type ClienteFormState = Record<string, string>;

function isCoberturaAutoActiva(row: { coberturaAutoAprobada?: boolean; cobertura_auto_aprobada?: boolean | number }): boolean {
  const v = row.coberturaAutoAprobada ?? row.cobertura_auto_aprobada;
  return v === true || v === 1;
}

const ESTADO_OPTIONS = [
  { value: "", label: "Todos (excl. eliminados)" },
  { value: "activo", label: "Activos" },
  { value: "pendiente", label: "Pendientes" },
  { value: "inactivo", label: "Inactivos" },
  { value: "eliminado", label: "Eliminados" },
  { value: "todos", label: "Todos" },
];

function estadoBadge(estado: string | undefined) {
  if (!estado) return <Badge variant="neutral">-</Badge>;
  switch (estado) {
    case "activo": return <Badge variant="success">Activo</Badge>;
    case "pendiente": return <Badge variant="warning">Pendiente</Badge>;
    case "inactivo": return <Badge variant="neutral">Inactivo</Badge>;
    case "eliminado": return <Badge variant="danger">Eliminado</Badge>;
    default: return <Badge variant="neutral">{estado}</Badge>;
  }
}

function sortModalidadOptions<T extends { tipo: string; label: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const tipoOrder = (tipo: string) => (tipo === "diario" ? 0 : 1);
    const byTipo = tipoOrder(a.tipo) - tipoOrder(b.tipo);
    if (byTipo !== 0) return byTipo;
    return a.label.localeCompare(b.label, "es");
  });
}

function toModalidadSelectOption(m: ModalidadOption): ModalidadSelectOption {
  return {
    value: m.id,
    label: m.nombre,
    tipo: m.tipo,
    precioBase: Number(m.precio ?? 0),
  };
}

function modalidadEstaVisible(modalidad: ModalidadOption): boolean {
  return modalidad.isVisible !== false && modalidad.is_visible !== 0;
}

const modalidadSelectStyles: StylesConfig<ModalidadSelectOption, true> = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    borderColor: state.isFocused ? "#1A3AB8" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(26, 58, 184, 0.2)" : "none",
    fontFamily: "Arial, Helvetica, sans-serif",
    minHeight: 42,
    "&:hover": { borderColor: "#1A3AB8" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    fontFamily: "Arial, Helvetica, sans-serif",
    zIndex: 50,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1A3AB8" : state.isFocused ? "#eef2ff" : "white",
    color: state.isSelected ? "white" : "#2C2C2C",
  }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 6,
    backgroundColor: "#eef2ff",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#1A3AB8",
    fontFamily: "Arial, Helvetica, sans-serif",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#1A3AB8",
    ":hover": { backgroundColor: "#1A3AB8", color: "white" },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
  }),
};

export function ClientesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ClienteRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState<"create" | "edit" | "detail" | null>(null);
  const [detailCliente, setDetailCliente] = useState<ClienteDetail | null>(null);
  const [detailTab, setDetailTab] = useState<"datos" | "modalidades" | "seguros">("datos");
  const [allModalidades, setAllModalidades] = useState<ModalidadOption[]>([]);
  const [segurosList, setSegurosList] = useState<SeguroRow[]>([]);
  const [form, setForm] = useState<ClienteFormState>({});
  const [coberturaAutoAprobada, setCoberturaAutoAprobada] = useState(false);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; name: string } | null>(null);
  const [confirmEstado, setConfirmEstado] = useState<{ id: number; estado: string } | null>(null);
  const [confirmRestaurar, setConfirmRestaurar] = useState<{ id: number; name: string } | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: "success" | "warning" | "error"; text: string } | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [modalidadesSeleccionadas, setModalidadesSeleccionadas] = useState<ModalidadSelectOption[]>([]);
  const modalidadesInicializadasClienteRef = useRef<number | null>(null);
  const [preciosPersonalizados, setPreciosPersonalizados] = useState<Record<number, string>>({});
  const [modalidadesFormError, setModalidadesFormError] = useState("");
  const [recibosIndividualesActivos, setRecibosIndividualesActivos] = useState(false);
  const [recibosMensualesActivos, setRecibosMensualesActivos] = useState(true);
  const [savingRecibosIndividuales, setSavingRecibosIndividuales] = useState(false);
  const [savingRecibosMensuales, setSavingRecibosMensuales] = useState(false);
  const [recibosConfigError, setRecibosConfigError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: 20 };
      if (search) params.search = search;
      if (estado) params.estado = estado;
      const res = await api.get<{ data: ClienteRow[]; total: number; page: number; totalPages: number }>(
        "/admin/clientes",
        { params }
      );
      setData(res.data.data ?? []);
      setTotal(res.data.total ?? 0);
      setPage(res.data.page ?? 1);
      setTotalPages(res.data.totalPages ?? 1);
    } catch {
      setData([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, search, estado]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const modalidadSelectOptions = useMemo(
    () => sortModalidadOptions(allModalidades.filter(modalidadEstaVisible).map(toModalidadSelectOption)),
    [allModalidades]
  );

  useEffect(() => {
    if (!detailCliente || detailTab !== "modalidades" || allModalidades.length === 0) return;
    const assigned = detailCliente.modalidades ?? [];
    const usarSeleccionInicialCompleta =
      assigned.length === 0 && modalidadesInicializadasClienteRef.current !== detailCliente.id;
    const selected = usarSeleccionInicialCompleta
      ? sortModalidadOptions(
          allModalidades
            .filter(modalidadEstaVisible)
            .map(toModalidadSelectOption)
        )
      : sortModalidadOptions(assigned.map((m) => {
        const catalog = allModalidades.find((c) => c.id === m.id);
        if (catalog) return toModalidadSelectOption(catalog);
        return {
          value: m.id,
          label: m.nombre,
          tipo: m.tipo,
          precioBase: Number(m.precio_base ?? m.precio ?? 0),
        };
      }));
    modalidadesInicializadasClienteRef.current = detailCliente.id;
    setModalidadesSeleccionadas(selected);
    const precios: Record<number, string> = {};
    for (const m of assigned) {
      const pp = m.precio_personalizado ?? m.precioPersonalizado;
      if (pp != null) precios[m.id] = String(pp);
    }
    setPreciosPersonalizados(precios);
    setModalidadesFormError("");
  }, [detailCliente, detailTab, allModalidades]);

  const openCreate = () => {
  navigate("/admin/crm?nueva=1");
};

  const openEdit = (row: ClienteRow) => {
    setEditId(row.id);
    setCoberturaAutoAprobada(isCoberturaAutoActiva(row));
    setForm({
      razon_social: (row.razonSocial ?? row.razon_social) ?? "",
      cif: row.cif ?? "",
      direccion: (row as Record<string, string>).direccion ?? "",
      poblacion: (row as Record<string, string>).poblacion ?? "",
      codigo_postal: (row as Record<string, string>).codigoPostal ?? (row as Record<string, string>).codigo_postal ?? "",
      pais: (row as Record<string, string>).pais ?? "España",
      persona_contacto: (row.personaContacto ?? row.persona_contacto) ?? "",
      telefono: row.telefono ?? "",
      email_contacto: (row as Record<string, string>).emailContacto ?? (row as Record<string, string>).email_contacto ?? "",
      numero_poliza: (row as Record<string, string>).numeroPoliza ?? (row as Record<string, string>).numero_poliza ?? "",
      metodo_pago: (row.metodoPago ?? row.metodo_pago) ?? "tpv",
    });
    setFormError("");
    setModalOpen("edit");
  };

  const openDetail = async (id: number) => {
    setModalOpen("detail");
    setDetailCliente(null);
    setDetailTab("datos");
    setRecibosConfigError(null);
    modalidadesInicializadasClienteRef.current = null;
    try {
      const [detailRes, modRes, configRes] = await Promise.all([
        api.get<ClienteDetail>(`/admin/clientes/${id}`),
        api.get<ModalidadOption[]>("/admin/modalidades", { params: { tipo: "todos", incluir_inactivas: "true" } }),
        api.get<ClientePreferenciasConfig>(`/admin/clientes-config/${id}/config`),
      ]);
      setDetailCliente(detailRes.data);
      setAllModalidades(modRes.data ?? []);
      setRecibosIndividualesActivos(Boolean(configRes.data.recibos_individuales_activos));
      setRecibosMensualesActivos(Boolean(configRes.data.recibos_mensuales_activos));
      const segRes = await api.get<SeguroRow[]>("/admin/seguros", { params: { cliente_id: id } });
      setSegurosList(Array.isArray(segRes.data) ? segRes.data : []);
    } catch {
      setDetailCliente(null);
    }
  };

  const handleRecibosIndividualesToggle = async (checked: boolean) => {
    if (!detailCliente?.id) return;
    setSavingRecibosIndividuales(true);
    setRecibosConfigError(null);
    try {
      const res = await api.put<ClientePreferenciasConfig>(
        `/admin/clientes-config/${detailCliente.id}/config`,
        { recibos_individuales_activos: checked }
      );
      setRecibosIndividualesActivos(Boolean(res.data.recibos_individuales_activos));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setRecibosConfigError(err.response?.data?.error ?? "No se pudo guardar la preferencia de recibos individuales.");
    } finally {
      setSavingRecibosIndividuales(false);
    }
  };

  const handleRecibosMensualesToggle = async (checked: boolean) => {
    if (!detailCliente?.id) return;
    setSavingRecibosMensuales(true);
    setRecibosConfigError(null);
    try {
      const res = await api.put<ClientePreferenciasConfig>(
        `/admin/clientes-config/${detailCliente.id}/config`,
        { recibos_mensuales_activos: checked }
      );
      setRecibosMensualesActivos(Boolean(res.data.recibos_mensuales_activos));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setRecibosConfigError(err.response?.data?.error ?? "No se pudo guardar la preferencia de recibos mensuales.");
    } finally {
      setSavingRecibosMensuales(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!form.email || !form.password || !form.razon_social || !form.cif || !form.direccion || !form.poblacion || !form.codigo_postal || !form.persona_contacto || !form.telefono || !form.email_contacto) {
      setFormError("Completa todos los campos obligatorios");
      return;
    }
    if (form.password.length < 8) {
      setFormError("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.post("/admin/clientes", {
        ...form,
        numero_poliza: form.numero_poliza || null,
      });
      setModalOpen(null);
      fetchList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setFormError(err.response?.data?.error ?? "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editId) return;
    if (!form.razon_social || !form.cif || !form.direccion || !form.poblacion || !form.codigo_postal || !form.persona_contacto || !form.telefono || !form.email_contacto) {
      setFormError("Completa todos los campos obligatorios");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.patch(`/admin/clientes/${editId}`, {
        razon_social: form.razon_social,
        cif: form.cif,
        direccion: form.direccion,
        poblacion: form.poblacion,
        codigo_postal: form.codigo_postal,
        pais: form.pais || "España",
        persona_contacto: form.persona_contacto,
        telefono: form.telefono,
        email_contacto: form.email_contacto,
        numero_poliza: form.numero_poliza || null,
        metodo_pago: form.metodo_pago,
        cobertura_auto_aprobada: form.metodo_pago === "transferencia" ? coberturaAutoAprobada : false,
      });
      if (detailCliente?.id === editId) {
        const res = await api.get<ClienteDetail>(`/admin/clientes/${editId}`);
        setDetailCliente(res.data);
      }
      setModalOpen(null);
      fetchList();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setFormError(err.response?.data?.error ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveModalidades = async () => {
    if (!detailCliente?.id) return;
    for (const m of modalidadesSeleccionadas) {
      const raw = preciosPersonalizados[m.value]?.trim();
      if (!raw) continue;
      const precio = parseFloat(raw);
      if (Number.isNaN(precio) || precio < m.precioBase) {
        setModalidadesFormError(
          `El precio de "${m.label}" no puede ser inferior al precio base (${m.precioBase.toFixed(2)} €)`
        );
        return;
      }
    }
    setModalidadesFormError("");
    setActionLoading("modalidades");
    try {
      const modalidadesPayload = modalidadesSeleccionadas.map((m) => {
        const item: { modalidad_id: number; precio_personalizado?: number } = { modalidad_id: m.value };
        const raw = preciosPersonalizados[m.value]?.trim();
        if (raw) {
          const precio = parseFloat(raw);
          if (!Number.isNaN(precio) && precio >= m.precioBase) {
            item.precio_personalizado = precio;
          }
        }
        return item;
      });
      await api.post(`/admin/clientes/${detailCliente.id}/modalidades`, { modalidades: modalidadesPayload });
      const res = await api.get<ClienteDetail>(`/admin/clientes/${detailCliente.id}`);
      setDetailCliente(res.data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setModalidadesFormError(err.response?.data?.error ?? "Error al guardar modalidades");
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnviarAcceso = async () => {
    if (!detailCliente?.id) return;
    setActionLoading("acceso");
    try {
      const res = await api.post<{ sent?: boolean; message?: string }>(
        `/admin/clientes/${detailCliente.id}/enviar-acceso`
      );
      const { sent, message } = res.data;
      if (sent === false) {
        setActionMessage({
          type: "warning",
          text: `El email no se ha podido enviar: ${message ?? ""}`.trim(),
        });
      } else {
        setActionMessage({ type: "success", text: message ?? "Operación realizada." });
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setActionMessage({
        type: "error",
        text: ax.response?.data?.error ?? "Error al enviar acceso",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuplantar = async () => {
    if (!detailCliente?.id) return;
    setActionLoading("suplantar");
    try {
      const res = await api.post<{ redirectTo: string }>(`/admin/clientes/${detailCliente.id}/suplantar`);
      navigate(res.data.redirectTo ?? "/dashboard");
    } finally {
      setActionLoading(null);
    }
  };

  const handleAprobarRechazar = async (id: number, action: "aprobar" | "rechazar") => {
    setActionLoading(`${action}-${id}`);
    try {
      await api.post(`/admin/clientes/${id}/${action}`);
      setConfirmEstado(null);
      if (detailCliente?.id === id) setDetailCliente((c) => (c ? { ...c, estado: action === "aprobar" ? "activo" : "inactivo" } : null));
      fetchList();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setActionLoading("delete");
    try {
      await api.delete(`/admin/clientes/${confirmDelete.id}`);
      setConfirmDelete(null);
      setModalOpen(null);
      setDetailCliente(null);
      fetchList();
    } finally {
      setActionLoading(null);
    }
  };

  const handleEstadoChange = async () => {
    if (!confirmEstado) return;
    setActionLoading("estado");
    try {
      await api.patch(`/admin/clientes/${confirmEstado.id}/estado`, { estado: confirmEstado.estado });
      setConfirmEstado(null);
      if (detailCliente?.id === confirmEstado.id) setDetailCliente((c) => (c ? { ...c, estado: confirmEstado.estado } : null));
      fetchList();
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestaurarConfirm = async () => {
    if (!confirmRestaurar) return;
    const id = confirmRestaurar.id;
    await api.patch(`/admin/clientes/${id}/estado`, { estado: "activo" });
    await fetchList();
    if (detailCliente?.id === id) {
      setDetailCliente((c) => (c ? { ...c, estado: "activo" } : null));
    }
    setConfirmRestaurar(null);
  };

  const handleExportarClientes = async () => {
    try {
      setExportLoading(true);
      const response = await api.get("/admin/clientes/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clientes-activos-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 401) {
        console.error("Error exportando clientes:", err);
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleModalidadesSelectChange = (options: MultiValue<ModalidadSelectOption>) => {
    const next = sortModalidadOptions([...(options ?? [])]);
    setModalidadesSeleccionadas(next);
    setPreciosPersonalizados((prev) => {
      const allowed = new Set(next.map((o) => o.value));
      const copy = { ...prev };
      for (const key of Object.keys(copy)) {
        if (!allowed.has(Number(key))) delete copy[Number(key)];
      }
      return copy;
    });
  };

  const columns = [
    { key: "empresa", header: "Empresa", render: (r: ClienteRow) => r.razonSocial ?? r.razon_social ?? "-" },
    { key: "cif", header: "CIF", render: (r: ClienteRow) => r.cif ?? "-" },
    { key: "contacto", header: "Contacto", render: (r: ClienteRow) => r.personaContacto ?? r.persona_contacto ?? "-" },
    { key: "telefono", header: "Teléfono", render: (r: ClienteRow) => r.telefono ?? "-" },
    { key: "estado", header: "Estado", render: (r: ClienteRow) => estadoBadge(r.estado) },
    { key: "metodo_pago", header: "Método pago", render: (r: ClienteRow) => (r.metodoPago ?? r.metodo_pago) === "transferencia" ? "Transferencia" : "TPV" },
    {
      key: "numeroPoliza",
      header: "Nº póliza",
      render: (r: ClienteRow) =>
        r.numeroPoliza ?? r.numero_poliza ?? (
          <span className="text-gray-400 text-sm">Sin póliza</span>
        ),
    },
    {
      key: "actions",
      header: "Acciones",
      render: (r: ClienteRow) => (
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => openDetail(r.id)}>Ver</Button>
          <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>Editar</Button>
          {r.estado === "eliminado" && (
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                setConfirmRestaurar({ id: r.id, name: r.razonSocial ?? r.razon_social ?? "" })
              }
            >
              <RotateCcw className="h-4 w-4 shrink-0" aria-hidden />
              Restaurar
            </Button>
          )}
          {r.estado !== "eliminado" && (
            <Button size="sm" variant="secondary" onClick={() => setConfirmEstado({ id: r.id, estado: (r.estado === "activo" ? "inactivo" : "activo") })}>
              {r.estado === "activo" ? "Desactivar" : "Activar"}
            </Button>
          )}
          {r.estado !== "eliminado" && (
            <Button size="sm" variant="danger" onClick={() => setConfirmDelete({ id: r.id, name: (r.razonSocial ?? r.razon_social) ?? "" })}>
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Clientes</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleExportarClientes} loading={exportLoading}>
            Exportar Excel
          </Button>
          <Button onClick={openCreate}>
  Nueva contratación
</Button>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <Input
          placeholder="Buscar por empresa, CIF, email..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="max-w-xs"
        />
        <Select
          options={ESTADO_OPTIONS}
          value={estado}
          onChange={(e) => { setEstado(e.target.value); setPage(1); }}
          className="w-48"
        />
      </div>

      <p className="text-sm text-gray-600">Total: {total} clientes</p>
      <Table<ClienteRow> columns={columns} data={data} loading={loading} emptyMessage="No hay clientes" />
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Modal Crear */}
      <Modal isOpen={modalOpen === "create"} onClose={() => setModalOpen(null)} title="Nuevo cliente" footer={
        <>
          <Button variant="secondary" onClick={() => setModalOpen(null)}>Cancelar</Button>
          <Button loading={saving} onClick={handleCreateSubmit}>Crear</Button>
        </>
      }>
        {formError && <p className="mb-4 text-sm text-red-600">{formError}</p>}
        <div className="space-y-4">
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
          <Input label="Contraseña" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required />
          <Input label="Razón social" value={form.razon_social} onChange={(e) => setForm((f) => ({ ...f, razon_social: e.target.value }))} required />
          <Input label="CIF" value={form.cif} onChange={(e) => setForm((f) => ({ ...f, cif: e.target.value }))} required />
          <Input label="Dirección" value={form.direccion} onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))} required />
          <Input label="Población" value={form.poblacion} onChange={(e) => setForm((f) => ({ ...f, poblacion: e.target.value }))} required />
          <Input label="Código postal" value={form.codigo_postal} onChange={(e) => setForm((f) => ({ ...f, codigo_postal: e.target.value }))} required />
          <Input label="País" value={form.pais} onChange={(e) => setForm((f) => ({ ...f, pais: e.target.value }))} />
          <Input label="Persona de contacto" value={form.persona_contacto} onChange={(e) => setForm((f) => ({ ...f, persona_contacto: e.target.value }))} required />
          <Input label="Teléfono" value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} required />
          <Input label="Email contacto" type="email" value={form.email_contacto} onChange={(e) => setForm((f) => ({ ...f, email_contacto: e.target.value }))} required />
          <Input label="Nº póliza" value={form.numero_poliza} onChange={(e) => setForm((f) => ({ ...f, numero_poliza: e.target.value }))} />
          <Select label="Método de pago" options={[{ value: "tpv", label: "TPV" }, { value: "transferencia", label: "Transferencia" }]} value={form.metodo_pago} onChange={(e) => setForm((f) => ({ ...f, metodo_pago: e.target.value }))} />
        </div>
      </Modal>

      {/* Modal Editar */}
      <Modal isOpen={modalOpen === "edit"} onClose={() => setModalOpen(null)} title="Editar cliente" footer={
        <>
          <Button variant="secondary" onClick={() => setModalOpen(null)}>Cancelar</Button>
          <Button loading={saving} onClick={handleEditSubmit}>Guardar</Button>
        </>
      }>
        {formError && <p className="mb-4 text-sm text-red-600">{formError}</p>}
        <div className="space-y-4">
          <Input label="Razón social" value={form.razon_social} onChange={(e) => setForm((f) => ({ ...f, razon_social: e.target.value }))} required />
          <Input label="CIF" value={form.cif} onChange={(e) => setForm((f) => ({ ...f, cif: e.target.value }))} required />
          <Input label="Dirección" value={form.direccion} onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))} required />
          <Input label="Población" value={form.poblacion} onChange={(e) => setForm((f) => ({ ...f, poblacion: e.target.value }))} required />
          <Input label="Código postal" value={form.codigo_postal} onChange={(e) => setForm((f) => ({ ...f, codigo_postal: e.target.value }))} required />
          <Input label="País" value={form.pais} onChange={(e) => setForm((f) => ({ ...f, pais: e.target.value }))} />
          <Input label="Persona de contacto" value={form.persona_contacto} onChange={(e) => setForm((f) => ({ ...f, persona_contacto: e.target.value }))} required />
          <Input label="Teléfono" value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} required />
          <Input label="Email contacto" type="email" value={form.email_contacto} onChange={(e) => setForm((f) => ({ ...f, email_contacto: e.target.value }))} required />
          <Input label="Nº póliza" value={form.numero_poliza} onChange={(e) => setForm((f) => ({ ...f, numero_poliza: e.target.value }))} />
          <Select label="Método de pago" options={[{ value: "tpv", label: "TPV" }, { value: "transferencia", label: "Transferencia" }]} value={form.metodo_pago} onChange={(e) => {
            const v = e.target.value;
            if (v === "tpv") setCoberturaAutoAprobada(false);
            setForm((f) => ({ ...f, metodo_pago: v }));
          }} />
          {form.metodo_pago === "transferencia" && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="auto_aprobada"
                checked={coberturaAutoAprobada}
                onChange={(e) => setCoberturaAutoAprobada(e.target.checked)}
              />
              <label htmlFor="auto_aprobada" className="text-sm text-gray-700">
                Cliente autorizado — activar cobertura automáticamente sin validación manual del admin
              </label>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal Detalle */}
      <Modal isOpen={modalOpen === "detail"} onClose={() => { setModalOpen(null); setDetailCliente(null); setActionMessage(null); }} title="Detalle del cliente" footer={null}>
        {!detailCliente ? (
          <div className="py-8 text-center text-gray-500">Cargando…</div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => openEdit(detailCliente as ClienteRow)}>Editar</Button>
              <Button
                size="sm"
                variant="secondary"
                loading={actionLoading === "acceso"}
                disabled={detailCliente.estado === "eliminado"}
                title={detailCliente.estado === "eliminado" ? "Restaura el cliente antes de enviar acceso" : undefined}
                onClick={handleEnviarAcceso}
              >
                Enviar acceso
              </Button>
              {detailCliente.estado === "eliminado" && (
                <span className="text-xs text-gray-500">Restaura el cliente antes de enviar acceso.</span>
              )}
              <Button size="sm" variant="primary" loading={actionLoading === "suplantar"} onClick={handleSuplantar}>Suplantar</Button>
              {detailCliente.estado === "eliminado" && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() =>
                    setConfirmRestaurar({
                      id: detailCliente.id,
                      name: (detailCliente.razonSocial ?? detailCliente.razon_social) ?? "",
                    })
                  }
                >
                  <RotateCcw className="h-4 w-4 shrink-0" aria-hidden />
                  Restaurar
                </Button>
              )}
              {detailCliente.estado === "pendiente" && (
                <>
                  <Button size="sm" variant="primary" loading={actionLoading === `aprobar-${detailCliente.id}`} onClick={() => handleAprobarRechazar(detailCliente.id, "aprobar")}>Aprobar</Button>
                  <Button size="sm" variant="danger" loading={actionLoading === `rechazar-${detailCliente.id}`} onClick={() => handleAprobarRechazar(detailCliente.id, "rechazar")}>Rechazar</Button>
                </>
              )}
            </div>
            {actionMessage && (
              <p
                className={`mb-4 rounded-lg px-3 py-2 text-sm ${
                  actionMessage.type === "success"
                    ? "bg-green-50 text-green-800"
                    : actionMessage.type === "warning"
                      ? "bg-orange-50 text-orange-800"
                      : "bg-red-50 text-red-800"
                }`}
              >
                {actionMessage.text}
              </p>
            )}
            <div className="flex gap-2 border-b border-gray-200">
              {(["datos", "modalidades", "seguros"] as const).map((tab) => (
                <button key={tab} type="button" onClick={() => setDetailTab(tab)} className={`border-b-2 px-4 py-2 text-sm font-medium ${detailTab === tab ? "border-[#1A3AB8] text-[#1A3AB8]" : "border-transparent text-gray-600 hover:text-[#2C2C2C]"}`}>
                  {tab === "datos" ? "Datos generales" : tab === "modalidades" ? "Modalidades" : "Seguros"}
                </button>
              ))}
            </div>
            {detailTab === "datos" && (
              <>
              <dl className="mt-4 grid gap-2 text-sm">
                <dt className="font-medium text-gray-500">Razón social</dt><dd>{(detailCliente.razonSocial ?? detailCliente.razon_social) ?? "-"}</dd>
                <dt className="font-medium text-gray-500">CIF</dt><dd>{detailCliente.cif ?? "-"}</dd>
                <dt className="font-medium text-gray-500">Email acceso</dt><dd>{detailCliente.email ?? "-"}</dd>
                <dt className="font-medium text-gray-500">Dirección</dt><dd>{detailCliente.direccion ?? "-"}</dd>
                <dt className="font-medium text-gray-500">Población</dt><dd>{detailCliente.poblacion ?? "-"}</dd>
                <dt className="font-medium text-gray-500">CP</dt><dd>{detailCliente.codigoPostal ?? detailCliente.codigo_postal ?? "-"}</dd>
                <dt className="font-medium text-gray-500">Contacto</dt><dd>{(detailCliente.personaContacto ?? detailCliente.persona_contacto) ?? "-"}</dd>
                <dt className="font-medium text-gray-500">Teléfono</dt><dd>{detailCliente.telefono ?? "-"}</dd>
                <dt className="font-medium text-gray-500">Método de pago</dt>
                <dd>
                  {(detailCliente.metodoPago ?? detailCliente.metodo_pago) === "transferencia"
                    ? "Transferencia"
                    : "TPV"}
                </dd>
                {(detailCliente.metodoPago ?? detailCliente.metodo_pago) === "transferencia" && (
                  <>
                    <dt className="font-medium text-gray-500">Cobertura automática</dt>
                    <dd>{isCoberturaAutoActiva(detailCliente) ? "Sí" : "No"}</dd>
                  </>
                )}
                <dt className="font-medium text-gray-500">Nº póliza</dt><dd>{detailCliente.numeroPoliza ?? detailCliente.numero_poliza ?? "-"}</dd>
                <dt className="font-medium text-gray-500">Estado</dt><dd>{estadoBadge(detailCliente.estado)}</dd>
              </dl>
              <div className="mt-6 space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="text-sm font-medium text-[#2C2C2C]">Recibos</h3>
                {recibosConfigError && (
                  <p className="text-sm text-red-600">{recibosConfigError}</p>
                )}
                <label className="flex items-start gap-3 text-sm text-[#2C2C2C]">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={recibosIndividualesActivos}
                    disabled={savingRecibosIndividuales}
                    onChange={(e) => void handleRecibosIndividualesToggle(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Recibos individuales por piloto</span>
                    <span className="mt-1 block text-gray-600">
                      Se genera un recibo cada vez que se activa un seguro
                    </span>
                    {savingRecibosIndividuales && <span className="mt-1 block text-gray-500">Guardando…</span>}
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm text-[#2C2C2C]">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={recibosMensualesActivos}
                    disabled={savingRecibosMensuales}
                    onChange={(e) => void handleRecibosMensualesToggle(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">Recibos mensuales (día 1 de cada mes)</span>
                    <span className="mt-1 block text-gray-600">
                      Resumen mensual con todos los seguros del mes anterior
                    </span>
                    {savingRecibosMensuales && <span className="mt-1 block text-gray-500">Guardando…</span>}
                  </span>
                </label>
              </div>
              </>
            )}
            {detailTab === "modalidades" && (
              <div className="mt-4 space-y-4">
                <div>
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-[#2C2C2C]">
                      Modalidades asignadas
                    </label>
                    <div className="flex items-center gap-3 text-xs">
                      <button
                        type="button"
                        className="font-medium text-[#173EBD] hover:underline"
                        onClick={() => setModalidadesSeleccionadas(modalidadSelectOptions)}
                      >
                        Seleccionar todas
                      </button>
                      <button
                        type="button"
                        className="font-medium text-gray-600 hover:underline"
                        onClick={() => setModalidadesSeleccionadas([])}
                      >
                        Quitar todas
                      </button>
                    </div>
                  </div>
                  <ReactSelect<ModalidadSelectOption, true>
                    isMulti
                    options={modalidadSelectOptions}
                    value={modalidadesSeleccionadas}
                    onChange={handleModalidadesSelectChange}
                    styles={modalidadSelectStyles}
                    placeholder="Selecciona modalidades..."
                    noOptionsMessage={() => "No hay modalidades disponibles"}
                    formatOptionLabel={(option) => (
                      <span>
                        {option.label} — {option.tipo === "diario" ? "📅 Diario" : "📆 Anual"}
                      </span>
                    )}
                    classNamePrefix="modalidad-select"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    En la primera configuración aparecen todas seleccionadas; quita únicamente las que no correspondan.
                  </p>
                </div>
                {modalidadesSeleccionadas.map((m) => (
                  <div key={m.value} className="flex flex-wrap items-center gap-3 mt-2">
                    <span className="text-sm w-48">
                      {m.label} — {m.tipo === "diario" ? "📅 Diario" : "📆 Anual"}
                    </span>
                    <span className="text-xs text-gray-500">Precio base: {m.precioBase.toFixed(2)}€</span>
                    <Input
                      type="number"
                      min={m.precioBase}
                      step="0.01"
                      placeholder={`${m.precioBase.toFixed(2)}€`}
                      value={preciosPersonalizados[m.value] ?? ""}
                      onChange={(e) =>
                        setPreciosPersonalizados((prev) => ({
                          ...prev,
                          [m.value]: e.target.value,
                        }))
                      }
                      className="w-28"
                    />
                    <span className="text-xs text-gray-400">€ (mín. {m.precioBase.toFixed(2)}€)</span>
                  </div>
                ))}
                {modalidadesFormError && <p className="text-sm text-red-600">{modalidadesFormError}</p>}
                <Button className="mt-4" loading={actionLoading === "modalidades"} onClick={handleSaveModalidades}>
                  Guardar modalidades
                </Button>
              </div>
            )}
            {detailTab === "seguros" && (
              <div className="mt-4">
                {segurosList.length === 0 ? <p className="text-gray-500">No hay seguros</p> : (
                  <ul className="space-y-2 text-sm">
                    {segurosList.map((s) => (
                      <li key={s.id}>#{s.id} {(s.eventoReferencia ?? s.evento_referencia)?.trim() || "Sin paquete"} · {(s.importeTotal ?? s.importe_total) ?? 0} € · {s.estado}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={handleDelete} title="Eliminar cliente" message={confirmDelete ? `¿Eliminar a ${confirmDelete.name}? Se realizará un borrado lógico.` : ""} confirmLabel="Eliminar" variant="danger" />
      <ConfirmDialog isOpen={!!confirmEstado} onClose={() => setConfirmEstado(null)} onConfirm={handleEstadoChange} title="Cambiar estado" message={confirmEstado ? `¿Cambiar estado a ${confirmEstado.estado}?` : ""} />
      <ConfirmDialog
        isOpen={!!confirmRestaurar}
        onClose={() => setConfirmRestaurar(null)}
        onConfirm={handleRestaurarConfirm}
        title="Restaurar cliente"
        message="El cliente dejará de estar eliminado, volverá a estar activo y podrá acceder de nuevo a la plataforma. ¿Confirmas?"
        confirmLabel="Restaurar"
        variant="primary"
      />
    </div>
  );
}
